import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Instagram, Mail, Check, Loader2 } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Reveal from "./Reveal";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../lib/pulaar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_IDS = ["disponibilite", "commande", "collaboration"];

export default function ContactSection() {
  const [params] = useSearchParams();
  const piece = params.get("piece");
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    type: piece ? "commande" : "disponibilite",
    message: piece
      ? `Bonjour, je souhaite commander la pièce "${piece}" en taille unique.`
      : "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (piece) {
      setForm((f) => ({
        ...f,
        type: "commande",
        message: `Bonjour, je souhaite commander la pièce "${piece}" en taille unique.`,
      }));
    }
  }, [piece]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        kind: form.type,
        message: form.message,
      });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="mx-auto max-w-[1300px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 scroll-mt-32"
    >
      <Reveal className="md:col-span-5">
        <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
          {t("contact.eyebrow")}
        </p>
        <h2 className="font-serif text-[36px] md:text-[56px] lg:text-[68px] leading-[0.95] tracking-[-0.02em] text-[#121212]">
          {t("contact.title")}
          <br />
          <span className="italic font-light">{t("contact.titleEm")}</span>
        </h2>
        <p className="mt-8 max-w-md text-base leading-[1.85] text-[#4A4641]">
          {t("contact.subtitle")}
        </p>

        <div className="mt-12 space-y-6">
          <a
            href="mailto:info@pulaar.co"
            data-testid="contact-email-link"
            className="group inline-flex items-baseline gap-3 font-serif text-xl md:text-2xl text-[#121212]"
          >
            <Mail size={16} strokeWidth={1.2} />
            <span className="link-underline">info@pulaar.co</span>
          </a>
          <br />
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-instagram-link"
            className="group inline-flex items-baseline gap-3 font-serif text-xl md:text-2xl text-[#121212]"
          >
            <Instagram size={16} strokeWidth={1.2} />
            <span className="link-underline">{INSTAGRAM_HANDLE}</span>
          </a>
        </div>
      </Reveal>

      <Reveal className="md:col-span-7" delay={0.15}>
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="bg-[#F3EFE7] p-8 md:p-12"
        >
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-6">
            {t("contact.kind")}
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {TYPE_IDS.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setForm({ ...form, type: id })}
                data-testid={`contact-type-${id}`}
                className={`h-10 px-4 text-[10px] tracking-[0.28em] uppercase border transition-colors duration-300 ${
                  form.type === id
                    ? "bg-[#121212] text-[#FBF8EF] border-[#121212]"
                    : "border-[#E5DFD3] text-[#121212] hover:border-[#5C4033]"
                }`}
              >
                {t(`contact.kinds.${id}`)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">
                {t("contact.name")}
              </span>
              <input
                required
                data-testid="contact-name"
                type="text"
                value={form.name}
                onChange={onChange("name")}
                className="mt-2 w-full h-12 bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif text-lg"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">
                {t("contact.email")}
              </span>
              <input
                required
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="mt-2 w-full h-12 bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif text-lg"
              />
            </label>
          </div>

          <label className="block mt-8">
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">
              {t("contact.message")}
            </span>
            <textarea
              required
              data-testid="contact-message"
              rows={6}
              value={form.message}
              onChange={onChange("message")}
              className="mt-2 w-full bg-transparent border-b border-[#5C4033]/30 focus:border-[#121212] outline-none text-[#121212] font-serif text-base leading-[1.7] py-2 resize-none"
            />
          </label>

          <button
            type="submit"
            disabled={loading || sent}
            data-testid="contact-submit"
            className="mt-10 inline-flex items-center justify-center h-12 px-10 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase hover:bg-[#5C4033] transition-colors duration-500 disabled:opacity-60"
          >
            {sent ? (
              <span className="inline-flex items-center gap-2">
                <Check size={14} strokeWidth={1.4} /> {t("contact.sent")}
              </span>
            ) : loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> {t("contact.sending")}
              </span>
            ) : (
              t("contact.submit")
            )}
          </button>
          {error && (
            <p className="mt-4 text-sm text-[#A33A2A]" data-testid="contact-error">
              {error}
            </p>
          )}
        </form>
      </Reveal>
    </section>
  );
}
